import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { WeeklyGrid } from '@/components/schedule/WeeklyGrid';
import type { Class } from '@pirouette/shared';

export const Route = createFileRoute('/parent/schedule')({
  component: ParentSchedule,
});

// Read-only weekly grid of all classes the parent's dancers are enrolled in.
// RLS does the heavy lifting: parent SELECT on enrollments returns only their
// own dancers' rows; joined classes come along.

function useParentSchedule() {
  return useQuery({
    queryKey: ['parent-schedule'],
    queryFn: async (): Promise<Class[]> => {
      const { data, error } = await supabase
        .from('enrollments')
        .select('classes(*)')
        .eq('status', 'enrolled')
        .is('deleted_at', null);
      if (error) throw error;
      interface Row {
        classes: Class | null;
      }
      const classes = ((data ?? []) as Row[])
        .map((r) => r.classes)
        .filter((c): c is Class => c !== null && c.deleted_at === null);
      // De-dupe: a parent with two dancers in the same class shouldn't see
      // the class drawn twice on the grid.
      const seen = new Set<string>();
      return classes.filter((c) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
    },
  });
}

function ParentSchedule() {
  const { data, isLoading } = useParentSchedule();

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Your week
        </p>
        <h1 className="mt-2">Schedule</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          All classes for the dancers on your account. Read-only — your studio admin manages the
          schedule.
        </p>
      </header>

      {isLoading && <p aria-live="polite">Loading…</p>}

      {data?.length === 0 && (
        <Empty
          title="No classes yet"
          description="Once your dancer is enrolled, their schedule will appear here."
        />
      )}

      {data && data.length > 0 && <WeeklyGrid classes={data} />}
    </div>
  );
}
